import { getGetAllAccountsQueryOptions } from '@/api/generated/accounts/accounts';
import { getGetAllPlansQueryOptions } from '@/api/generated/flow/flow';
import { getGetAllGoalsQueryOptions } from '@/api/generated/goals/goals';
import { getGetAllTransactionsQueryOptions } from '@/api/generated/transactions/transactions';
import { prefetchRoute } from '@/lib/routePrefetch';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Warms the *data* a route needs, alongside `prefetchRoute` (which warms the JS
 * chunk). Each thunk prefetches one query using the same `getXxxQueryOptions()`
 * the page's Orval hook uses with no params, so the prefetch populates the exact
 * cache entry the page then reads — navigation lands on warm data, not a spinner.
 * Each thunk calls `prefetchQuery` directly so generics infer per-query (type-safe).
 */
type RouteQueryPrefetch = (queryClient: QueryClient) => Promise<unknown>;

const prefetchAccounts: RouteQueryPrefetch = (qc) =>
  qc.prefetchQuery(getGetAllAccountsQueryOptions());
const prefetchTransactions: RouteQueryPrefetch = (qc) =>
  qc.prefetchQuery(getGetAllTransactionsQueryOptions());
const prefetchGoals: RouteQueryPrefetch = (qc) => qc.prefetchQuery(getGetAllGoalsQueryOptions());
const prefetchPlans: RouteQueryPrefetch = (qc) => qc.prefetchQuery(getGetAllPlansQueryOptions());

const routeQueryPrefetchers: Record<string, RouteQueryPrefetch[]> = {
  '/overview': [prefetchAccounts, prefetchTransactions],
  '/banking': [prefetchAccounts],
  '/banking/accounts': [prefetchAccounts],
  '/banking/transactions': [prefetchTransactions],
  '/overview/net-worth': [prefetchAccounts, prefetchTransactions],
  '/planning': [prefetchGoals, prefetchPlans],
  '/planning/goals': [prefetchGoals],
};

/** The pillar landing routes worth warming during idle time after boot. */
export const SIBLING_PILLAR_ROUTES = ['/overview', '/banking/accounts', '/planning'] as const;

/** Skip prefetch on data-saver / 2G so we never spend a constrained user's bytes speculatively. */
function shouldSkipForNetwork(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
}

/** Prefetch the queries a route reads. Cheap and idempotent (respects `staleTime`). */
export function prefetchRouteData(queryClient: QueryClient, path: string): void {
  if (shouldSkipForNetwork()) return;
  const prefetchers = routeQueryPrefetchers[path];
  if (!prefetchers) return;
  for (const prefetchQuery of prefetchers) {
    void prefetchQuery(queryClient);
  }
}

/**
 * Returns a stable callback that warms both the route's JS chunk and its data.
 * Wire to hover/touch on nav links and tabs so a tap lands on a ready screen.
 */
export function useRoutePrefetch(): (path: string) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (path: string) => {
      prefetchRoute(path);
      prefetchRouteData(queryClient, path);
    },
    [queryClient]
  );
}
